import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useApp } from '../context/AppContext';
import Layout from '../components/layout/Layout';
import Header from '../components/layout/Header';
import CompareKPICard from '../components/dashboard/CompareKPICard';
import CompareBarChart from '../components/dashboard/CompareBarChart';
import { Skeleton } from '../components/ui/skeleton';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Building2, 
  Shield, 
  Megaphone, 
  DollarSign, 
  Users,
  GitCompare,
  ArrowRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ComparePage = () => {
  const { periodType, selectedPeriod, years } = useApp();
  const [loading, setLoading] = useState(false);
  const [comparisonData, setComparisonData] = useState(null);
  const [selectedFunction, setSelectedFunction] = useState('all');
  const [year1, setYear1] = useState(2024);
  const [year2, setYear2] = useState(2025);

  const functionIcons = {
    brokerage: Building2,
    mga: Shield,
    marketing: Megaphone,
    finance: DollarSign,
    people: Users
  };

  const functionLabels = {
    brokerage: 'Brokerage',
    mga: 'MGA',
    marketing: 'Marketing',
    finance: 'Finance',
    people: 'People'
  };

  const fetchComparison = useCallback(async () => {
    if (!selectedPeriod) return;
    
    setLoading(true);
    try {
      const endpoint = selectedFunction === 'all' 
        ? `${API}/dashboard/compare/all`
        : `${API}/dashboard/compare/${selectedFunction}`;
      
      const response = await axios.get(endpoint, {
        params: {
          period_type: periodType,
          year1,
          year2,
          period_start: selectedPeriod?.startDate,
          period_end: selectedPeriod?.endDate
        }
      });
      setComparisonData(response.data);
    } catch (error) {
      console.error('Failed to fetch comparison:', error);
    } finally {
      setLoading(false);
    }
  }, [selectedFunction, periodType, year1, year2, selectedPeriod]);

  useEffect(() => {
    fetchComparison();
  }, [fetchComparison]);

  const getPeriodLabel = () => {
    if (!selectedPeriod) return '';
    if (periodType === 'yearly') return 'Full Year';
    return selectedPeriod.shortLabel;
  };

  const renderFunctionComparison = (funcName, data) => {
    const Icon = functionIcons[funcName] || Building2;
    
    return (
      <div key={funcName} className="bg-white rounded-lg border border-[#E5E7EB] p-4 sm:p-6 mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-[#F3F4F6] flex items-center justify-center">
            <Icon className="w-5 h-5 text-[#6B7280]" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-[#111827]">{functionLabels[funcName]}</h2>
            <p className="text-sm text-[#6B7280]">{data.length} metrics compared</p>
          </div>
        </div>

        {/* KPI Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          {data.slice(0, 4).map((item) => (
            <CompareKPICard
              key={item.metric_name}
              metricName={item.metric_name}
              unit={item.unit}
              directionality={item.directionality}
              year1={year1}
              year2={year2}
              value1={item[`year_${year1}`]?.actual}
              value2={item[`year_${year2}`]?.actual}
              plan1={item[`year_${year1}`]?.plan}
              plan2={item[`year_${year2}`]?.plan}
              change={item.change}
              changePct={item.change_pct}
            />
          ))}
        </div>

        {/* Bar Chart */}
        <CompareBarChart
          data={data}
          year1={year1}
          year2={year2}
          title={`${functionLabels[funcName]} - YoY Comparison`}
        />
      </div>
    );
  };

  if (loading) {
    return (
      <Layout>
        <Header title="Period Comparison" subtitle={`Comparing ${getPeriodLabel()} across years`} />
        <div className="p-4 sm:p-6 space-y-6">
          <Skeleton className="h-12 w-full max-w-xl" />
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-40 rounded-lg" />
            ))}
          </div>
          <Skeleton className="h-72 rounded-lg" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <Header title="Period Comparison" subtitle={`Comparing ${getPeriodLabel()} across years`} />
      
      <div className="p-4 sm:p-6 space-y-6" data-testid="compare-page">
        {/* Comparison Controls */}
        <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Function Selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[#6B7280]">Function:</span>
              <Select value={selectedFunction} onValueChange={setSelectedFunction}>
                <SelectTrigger className="w-[150px]" data-testid="function-selector">
                  <SelectValue placeholder="Select function" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Functions</SelectItem>
                  <SelectItem value="brokerage">Brokerage</SelectItem>
                  <SelectItem value="mga">MGA</SelectItem>
                  <SelectItem value="marketing">Marketing</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                  <SelectItem value="people">People</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Year Comparison */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-[#6B7280]">Compare:</span>
              <Select value={String(year1)} onValueChange={(v) => setYear1(Number(v))}>
                <SelectTrigger className="w-[90px]" data-testid="year1-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="flex items-center gap-1 px-2">
                <ArrowRight className="w-4 h-4 text-[#9CA3AF]" />
              </div>
              
              <Select value={String(year2)} onValueChange={(v) => setYear2(Number(v))}>
                <SelectTrigger className="w-[90px]" data-testid="year2-selector">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((y) => (
                    <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Info Badge */}
            <div className="flex items-center gap-2 bg-[#EFF6FF] text-[#2563EB] px-3 py-1.5 rounded-full text-sm">
              <GitCompare className="w-4 h-4" />
              <span>Same period, different years</span>
            </div>
          </div>
        </div>

        {/* Summary Stats */}
        {comparisonData && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
              <p className="text-sm font-medium text-[#6B7280] mb-1">Period</p>
              <p className="text-xl font-bold text-[#111827]">{getPeriodLabel()}</p>
            </div>
            <div className="bg-white rounded-lg border border-[#E5E7EB] p-4">
              <p className="text-sm font-medium text-[#6B7280] mb-1">Comparing</p>
              <p className="text-xl font-bold text-[#111827]">{year1} → {year2}</p>
            </div>
            <div className="bg-[#D1FAE5] rounded-lg border border-[#34D399] p-4">
              <p className="text-sm font-medium text-[#065F46] mb-1">Improved</p>
              <p className="text-xl font-bold text-[#065F46]">
                {selectedFunction === 'all' 
                  ? Object.values(comparisonData.functions || {}).flat().filter(m => {
                      const isImproved = m.directionality === 'lower_is_better' 
                        ? m.change_pct < 0 
                        : m.change_pct > 0;
                      return m.change_pct !== null && isImproved;
                    }).length
                  : comparisonData.comparison?.filter(m => {
                      const isImproved = m.directionality === 'lower_is_better' 
                        ? m.change_pct < 0 
                        : m.change_pct > 0;
                      return m.change_pct !== null && isImproved;
                    }).length || 0
                }
              </p>
            </div>
            <div className="bg-[#FEE2E2] rounded-lg border border-[#FCA5A5] p-4">
              <p className="text-sm font-medium text-[#991B1B] mb-1">Declined</p>
              <p className="text-xl font-bold text-[#991B1B]">
                {selectedFunction === 'all' 
                  ? Object.values(comparisonData.functions || {}).flat().filter(m => {
                      const isDeclined = m.directionality === 'lower_is_better' 
                        ? m.change_pct > 0 
                        : m.change_pct < 0;
                      return m.change_pct !== null && isDeclined;
                    }).length
                  : comparisonData.comparison?.filter(m => {
                      const isDeclined = m.directionality === 'lower_is_better' 
                        ? m.change_pct > 0 
                        : m.change_pct < 0;
                      return m.change_pct !== null && isDeclined;
                    }).length || 0
                }
              </p>
            </div>
          </div>
        )}

        {/* Comparison Content */}
        {comparisonData && (
          <>
            {selectedFunction === 'all' ? (
              // All functions view
              <Tabs defaultValue="brokerage" className="w-full">
                <TabsList className="w-full flex-wrap h-auto gap-1 p-1">
                  {Object.keys(comparisonData.functions || {}).map((func) => (
                    <TabsTrigger key={func} value={func} className="capitalize">
                      {functionLabels[func]}
                    </TabsTrigger>
                  ))}
                </TabsList>
                
                {Object.entries(comparisonData.functions || {}).map(([func, data]) => (
                  <TabsContent key={func} value={func} className="mt-4">
                    {renderFunctionComparison(func, data)}
                  </TabsContent>
                ))}
              </Tabs>
            ) : (
              // Single function view
              renderFunctionComparison(selectedFunction, comparisonData.comparison || [])
            )}
          </>
        )}

        {/* Empty State */}
        {!comparisonData && !loading && (
          <div className="bg-white rounded-lg border border-[#E5E7EB] p-12 text-center">
            <GitCompare className="w-12 h-12 text-[#D1D5DB] mx-auto mb-4" />
            <h3 className="text-lg font-medium text-[#111827] mb-2">No comparison data</h3>
            <p className="text-sm text-[#6B7280]">
              Select a period and years to compare KPI performance.
            </p>
          </div>
        )}

        {/* Last Updated */}
        {comparisonData?.last_updated && (
          <p className="text-sm text-[#9CA3AF] text-right">
            Last updated: {new Date(comparisonData.last_updated).toLocaleString()}
          </p>
        )}
      </div>
    </Layout>
  );
};

export default ComparePage;
